import {
  ArrowUpRight,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Settings,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import posthog from 'posthog-js';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ANALYTICS_EVENTS } from 'src/analytics/events';
import { useClients } from 'src/context/ClientContext';
import { useSubscription } from 'src/context/SubscriptionContext';
import { useSidebar } from 'src/pages/dashboard/context/SidebarContext';
import { subscriptionService } from 'src/services/subscriptionService';
import BrandLogo from 'src/shared/components/BrandLogo';
import TierBadge from 'src/shared/components/TierBadge';
import {
  ActionButton,
  ContainerBox,
  FlexContainer,
  IconButton,
  LoadingSpinner,
  StackContainer,
  TypographyText,
  useMobileBreakpoint,
} from 'src/tef/components';

import ClientListItem from './ClientListItem';
import EmptyStateMessage from './EmptyStateMessage';

interface DashboardSidebarProps {
  onLogout: () => void;
}

const SIDEBAR_CLIENT_LIMIT = 50;
const BOOK_DEMO_URL = 'https://app.apollo.io/#/meet/v3r-p4m-cm2/30-min';

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, isMobileOpen, toggleSidebar, setMobileOpen } = useSidebar();
  const { clients, loading } = useClients();
  const { plan, hasUpgradeOffer, hasRequestedUpgrade, isTrialing, refreshSubscription } = useSubscription();
  const tierDisplayName = isTrialing ? 'Trial' : plan?.display_name || '';
  const [requestingUpgrade, setRequestingUpgrade] = useState(false);
  const isMobile = useMobileBreakpoint();
  const [showAllClients, setShowAllClients] = useState(false);

  const sortedClients = useMemo(() => {
    const statusOrder: Record<string, number> = { active: 0, prospect: 1, pending: 1, inactive: 2 };
    return [...clients].sort((a, b) => (statusOrder[a.status] ?? 1) - (statusOrder[b.status] ?? 1));
  }, [clients]);

  const handleUpgrade = async () => {
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000));
    try {
      const url = await Promise.race([subscriptionService.createCheckoutSession(null, 'usd'), timeout]);
      window.location.href = url;
    } catch {
      navigate('/settings');
    }
  };

  const handleRequestUpgrade = async () => {
    if (requestingUpgrade) return;
    setRequestingUpgrade(true);
    window.open(BOOK_DEMO_URL, '_blank', 'noopener,noreferrer');
    try {
      await subscriptionService.requestUpgrade();
      await refreshSubscription();
    } catch {
      // Booking URL already opened; DB request is best-effort
    } finally {
      setRequestingUpgrade(false);
    }
  };

  const isDashboardActive = location.pathname === '/dashboard';
  const isSettingsActive = location.pathname === '/settings';
  const isHelpActive = location.pathname === '/help';

  const closeMobileAndNavigate = (path: string) => {
    if (isMobile) setMobileOpen(false);
    navigate(path);
  };

  const handleDashboardClick = () => {
    posthog?.capture(ANALYTICS_EVENTS.NAV_DASHBOARD);
    if (!isDashboardActive) closeMobileAndNavigate('/dashboard');
    else if (isMobile) setMobileOpen(false);
  };

  const handleNewClient = () => {
    posthog?.capture(ANALYTICS_EVENTS.NAV_NEW_CLIENT);
    closeMobileAndNavigate('/new-client');
  };

  const handleSettings = () => {
    posthog?.capture(ANALYTICS_EVENTS.NAV_SETTINGS);
    closeMobileAndNavigate('/settings');
  };

  const handleHelp = () => {
    closeMobileAndNavigate('/help');
  };

  const handleShowMeAround = () => {
    localStorage.setItem('tropel_relaunch_tour', '1');
    if (isMobile) setMobileOpen(false);
    window.location.assign('/dashboard');
  };

  const handleLogout = () => {
    posthog?.capture(ANALYTICS_EVENTS.LOGOUT);
    if (isMobile) setMobileOpen(false);
    onLogout();
  };

  const expandedContent = (
    <ContainerBox className="h-full flex flex-col">
      <ContainerBox className="px-3 py-4 border-b border-slate-200 dark:border-slate-700">
        <FlexContainer align="start" justify="between">
          <FlexContainer align="center" gap="sm">
            <BrandLogo variant="auto" size="sm" withClearSpace={false} />
            {plan && <TierBadge planName={plan.name} displayName={tierDisplayName} mini />}
          </FlexContainer>
          {isMobile && (
            <IconButton
              icon={<X className="w-5 h-5" />}
              variant={{ style: 'ghost', size: 'sm' }}
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            />
          )}
        </FlexContainer>
      </ContainerBox>

      <ContainerBox className="flex-1 min-h-0 flex flex-col">
        <StackContainer spacing="md" className="p-3 pt-4 flex-1 min-h-0 flex flex-col">
          <ActionButton
            onClick={handleDashboardClick}
            variant={{ style: isDashboardActive ? 'primary' : 'ghost', size: 'md' }}
            className={`w-full justify-start ${isDashboardActive ? 'border' : ''}`}
            icon={<LayoutDashboard className="w-5 h-5" />}
            aria-label="Dashboard"
            aria-current={isDashboardActive ? 'page' : undefined}
          >
            Dashboard
          </ActionButton>

          <ContainerBox className="flex-1 min-h-0 flex flex-col" data-tutorial="sidebar-clients">
            <FlexContainer justify="between" align="center" className="mb-3 px-3 shrink-0">
              <FlexContainer align="center" gap="sm">
                <Users className="w-5 h-5" />
                <TypographyText variant={{ size: 'base' }} className="font-semibold">
                  Clients
                </TypographyText>
              </FlexContainer>
              <IconButton
                icon={<Plus className="w-4 h-4" />}
                variant={{ style: 'ghost', size: 'xs' }}
                onClick={handleNewClient}
                aria-label="Add client"
                data-tutorial="sidebar-add-client"
              />
            </FlexContainer>

            <StackContainer spacing="xs" className="flex-1 min-h-0 overflow-y-auto px-1">
              {loading ? (
                <ContainerBox className="flex items-center justify-center py-8">
                  <LoadingSpinner variant={{ size: 'md' }} />
                </ContainerBox>
              ) : clients.length === 0 ? (
                <EmptyStateMessage type="clients" message="No clients yet" />
              ) : (
                <>
                  {(showAllClients ? sortedClients : sortedClients.slice(0, SIDEBAR_CLIENT_LIMIT)).map((client) => {
                    const isClientSelected = location.pathname === `/client/${client.id}`;
                    return (
                      <ContainerBox
                        key={client.id}
                        onClick={() => {
                          if (isMobile) setMobileOpen(false);
                        }}
                        className={isClientSelected ? 'bg-slate-100 dark:bg-slate-700 rounded-lg' : ''}
                      >
                        <ClientListItem client={client} />
                      </ContainerBox>
                    );
                  })}
                  {!showAllClients && clients.length > SIDEBAR_CLIENT_LIMIT && (
                    <ActionButton
                      variant={{ style: 'ghost', size: 'sm' }}
                      onClick={() => setShowAllClients(true)}
                      className="w-full text-xs"
                    >
                      Show all ({clients.length})
                    </ActionButton>
                  )}
                </>
              )}
            </StackContainer>
          </ContainerBox>
        </StackContainer>
      </ContainerBox>

      <StackContainer
        spacing="xs"
        className="p-3 border-t border-slate-200 dark:border-slate-700 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      >
        {plan && hasUpgradeOffer && (
          <ContainerBox className="mx-0 mb-1 p-3 rounded-lg bg-gradient-to-r from-primary to-primary-hover">
            <TypographyText variant={{ size: 'xs' }} className="font-semibold text-white">
              Your Plan Upgrade Is Ready
            </TypographyText>
            <TypographyText variant={{ size: 'xs' }} className="text-white/60 mt-0.5">
              Our team set up a plan for you. Activate it to keep going.
            </TypographyText>
            <ActionButton
              variant={{ style: 'ghost', size: 'xs' }}
              className="w-full mt-2 !bg-white/10 !text-white hover:!bg-white/20 !border-white/20"
              onClick={handleUpgrade}
              icon={<ArrowUpRight className="w-3 h-3" />}
              iconPosition="right"
            >
              Get Started
            </ActionButton>
          </ContainerBox>
        )}
        {plan && isTrialing && !hasUpgradeOffer && !hasRequestedUpgrade && (
          <ContainerBox className="mx-0 mb-1 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <TypographyText variant={{ size: 'xs' }} className="font-semibold text-slate-900 dark:text-slate-100">
              Enjoying Tropel?
            </TypographyText>
            <TypographyText variant={{ size: 'xs' }} className="text-slate-500 dark:text-slate-400 mt-0.5">
              Send our team a request to upgrade your account. You can optionally pick a time to chat.
            </TypographyText>
            <ActionButton
              variant={{ style: 'primary', size: 'xs' }}
              className="w-full mt-2"
              onClick={handleRequestUpgrade}
              loading={requestingUpgrade}
              disabled={requestingUpgrade}
              icon={<ArrowUpRight className="w-3 h-3" />}
              iconPosition="right"
            >
              Send Request & Schedule
            </ActionButton>
          </ContainerBox>
        )}
        {plan && hasRequestedUpgrade && (
          <ContainerBox className="mx-0 mb-1 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20">
            <TypographyText variant={{ size: 'xs' }} className="font-semibold text-emerald-700 dark:text-emerald-300">
              Request Received
            </TypographyText>
            <TypographyText variant={{ size: 'xs' }} className="text-emerald-600 dark:text-emerald-400 mt-0.5">
              Our team will reach out shortly.
            </TypographyText>
          </ContainerBox>
        )}
        {clients.length === 0 && (
          <ActionButton
            onClick={handleShowMeAround}
            variant={{ style: 'ghost', size: 'md' }}
            className="w-full justify-start"
            icon={<Sparkles className="w-5 h-5" />}
            aria-label="Show me around"
          >
            Show me around
          </ActionButton>
        )}
        <ActionButton
          onClick={handleHelp}
          variant={{ style: isHelpActive ? 'primary' : 'ghost', size: 'md' }}
          className="w-full justify-start"
          icon={<HelpCircle className="w-5 h-5" />}
          aria-label="Help"
        >
          Help
        </ActionButton>
        <ActionButton
          onClick={handleSettings}
          variant={{ style: isSettingsActive ? 'primary' : 'ghost', size: 'md' }}
          className="w-full justify-start"
          icon={<Settings className="w-5 h-5" />}
          aria-label="Settings"
        >
          Settings
        </ActionButton>
        <ActionButton
          onClick={handleLogout}
          variant={{ style: 'ghost', size: 'md' }}
          className="w-full justify-start"
          icon={<LogOut className="w-5 h-5" />}
          aria-label="Logout"
        >
          Logout
        </ActionButton>
      </StackContainer>
    </ContainerBox>
  );

  const collapsedContent = (
    <ContainerBox className="h-full flex flex-col">
      <ContainerBox className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-center">
        <IconButton
          icon={<Menu className="w-5 h-5" />}
          variant={{ style: 'ghost', size: 'sm' }}
          onClick={toggleSidebar}
          aria-label="Expand sidebar"
        />
      </ContainerBox>

      <StackContainer spacing="sm" className="flex-1 py-3 flex flex-col items-center">
        <IconButton
          icon={<LayoutDashboard className="w-5 h-5" />}
          variant={{ style: isDashboardActive ? 'primary' : 'ghost', size: 'md' }}
          onClick={handleDashboardClick}
          aria-label="Dashboard"
          aria-current={isDashboardActive ? 'page' : undefined}
          className={isDashboardActive ? 'border' : ''}
        />
        <IconButton
          icon={<Users className="w-5 h-5" />}
          variant={{ style: 'ghost', size: 'md' }}
          onClick={toggleSidebar}
          aria-label="Clients"
        />
      </StackContainer>

      <StackContainer
        spacing="sm"
        className="py-3 border-t border-slate-200 dark:border-slate-700 flex flex-col items-center pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      >
        {plan && <TierBadge planName={plan.name} displayName={tierDisplayName} compact />}
        {clients.length === 0 && (
          <IconButton
            icon={<Sparkles className="w-5 h-5" />}
            variant={{ style: 'ghost', size: 'md' }}
            onClick={handleShowMeAround}
            aria-label="Show me around"
          />
        )}
        <IconButton
          icon={<HelpCircle className="w-5 h-5" />}
          variant={{ style: isHelpActive ? 'primary' : 'ghost', size: 'md' }}
          onClick={handleHelp}
          aria-label="Help"
          className={isHelpActive ? 'border' : ''}
        />
        <IconButton
          icon={<Settings className="w-5 h-5" />}
          variant={{ style: isSettingsActive ? 'primary' : 'ghost', size: 'md' }}
          onClick={handleSettings}
          aria-label="Settings"
          className={isSettingsActive ? 'border' : ''}
        />
        <IconButton
          icon={<LogOut className="w-5 h-5" />}
          variant={{ style: 'ghost', size: 'md' }}
          onClick={handleLogout}
          aria-label="Logout"
        />
      </StackContainer>
    </ContainerBox>
  );

  if (isMobile) {
    if (!isMobileOpen) return null;
    return <ContainerBox className="fixed inset-0 z-50 bg-white dark:bg-slate-800">{expandedContent}</ContainerBox>;
  }

  return (
    <ContainerBox
      className={`${isCollapsed ? 'w-16' : 'w-[248px]'} h-full border-r border-slate-200 dark:border-slate-700 flex flex-col transition-all duration-300 ease-in-out`}
    >
      {isCollapsed ? collapsedContent : expandedContent}
    </ContainerBox>
  );
};

export default DashboardSidebar;
